import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";
import HistoricChatModal from "../../../ReportClosed/Modals/HistoricChatModal";

// Utils
import dataHandler from "@Utils/dataHandler";
import { getCallDurationLabel } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const DetailedTable = ({ dispatch, userName, companyName, onOpenFilterModal, appliedFilters = {} }) => {
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [selectedChatData, setSelectedChatData] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const defaultParams = { page: 1, orderDir: "desc", rows: 20 };
	const [tableParams, setTableParams] = useState(defaultParams);
	const tableFields = "id,group.name,user.fullName,contact.name,channel:platform.name,channel.label";

	useEffect(() => {
		(async () => {
			try {
				setTableParams(defaultParams);
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "/reports/historic/detailed/avgTimes",
					params: { ...defaultParams, ...appliedFilters, fields: tableFields },
				});

				setFilteredItems(response.items);
				setFilteredCount(response.count);
			} catch (error) {
				setFilteredItems([]);
				setFilteredCount(0);
			}
		})();
	}, [appliedFilters]);

	const handlerData = async (params = defaultParams, printable) => {
		if (!printable) {
			setTableParams(params);
		}
		const data = await dataHandler({
			method: "GET",
			dispatch,
			url: "/reports/historic/detailed/avgTimes",
			params: { ...params, ...appliedFilters, fields: tableFields },
		});
		if (printable) {
			setFilteredPrintData(data.items);
		} else {
			setFilteredItems(data.items);
			setFilteredCount(data.count);
		}
	};

	const handleRefreshData = () => {
		setTableParams(defaultParams);
		handlerData({ ...defaultParams, ...appliedFilters });
	};

	const renderTopButtons = () => {
		const hasFilters = Object.keys(appliedFilters || {}).length > 0;
		const filterText = hasFilters ? "Filtrando" : "";

		const buttons = [
			{
				icon: "filter",
				className: hasFilters ? styles.filterButtonActive : styles.filterButton,
				onClick: onOpenFilterModal,
				content: filterText,
				label: filterText,
				showLabel: true,
				componentType: "button",
			},
			{
				icon: "refresh",
				className: styles.refreshButton,
				onClick: handleRefreshData,
			},
		];

		const dateRange = appliedFilters?.createdDate || appliedFilters?.closeDate;
		if (dateRange) {
			const formatD = (d) => {
				if (!d) return "";
				const parts = d.split("-");
				if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
				return d;
			};
			const [start, end] = dateRange.split(",");
			let value = formatD(start);
			const today = new Date();
			const currentEnd =
				end || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
			if (currentEnd && start !== currentEnd) value += ` al ${formatD(currentEnd)}`;

			buttons.push({
				componentType: "Label",
				title: "Fecha:",
				value,
			});
		}

		return buttons;
	};

	const handleChatClick = (item) => {
		setSelectedChatData(item);
		setIsModalOpen(true);
	};

	const handleChatKeyDown = (e, item) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setSelectedChatData(item);
			setIsModalOpen(true);
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.containerTableGroup}>
				<div className={styles.tableGroupList}>
					<IkTable
						data={filteredItems}
						columns={[
							{
								label: "Chat ID",
								key: "id",
								format: "string",
								headerPosition: "center",
								dataPosition: "center",
								width: 1,
								render: (item) => {
									const id = item.chatId || item.id;
									return (
										<span
											className={styles.clickableChatId}
											role="button"
											tabIndex={0}
											onClick={() => {
												return handleChatClick(item);
											}}
											onKeyDown={(e) => {
												return handleChatKeyDown(e, item);
											}}>
											{id}
										</span>
									);
								},
							},
							{ label: "Grupo", key: "group.name", format: "string", dataPosition: "left", width: 1 },
							{ label: "Usuario", key: "user.fullName", format: "string", dataPosition: "left", width: 1 },
							{ label: "Contacto", key: "contact.name", format: "string", dataPosition: "left", width: 1 },
							{ label: "Telefono", key: "contact.phone", format: "phone", dataPosition: "center", width: 0.5 },
							{ label: "F. Entrada", key: "createdDate", format: "dateTime", dataPosition: "center", width: 0.5 },
							{ label: "F. Cierre", key: "closeDate", format: "dateTime", dataPosition: "center", width: 0.5 },
							{
								label: "Tiempo 1ra Resp.",
								key: "avgFirstResponse",
								format: "string",
								dataPosition: "center",
								width: 1,
								render: (data) => {
									if (!data.avgFirstResponse || data.avgFirstResponse === 0) return "";
									return getCallDurationLabel(data.avgFirstResponse);
								},
							},
							{
								label: "Tiempo Atención",
								key: "avgResponse",
								format: "string",
								dataPosition: "center",
								width: 1,
								render: (data) => {
									if (!data.avgResponse || data.avgResponse === 0) return "";
									return getCallDurationLabel(data.avgResponse);
								},
								canSort: false,
								canFilter: false,
							},

							{
								label: "Tiempo Total",
								key: "avgChatTime",
								format: "string",
								dataPosition: "left",
								width: 1,
								render: (data) => {
									return getCallDurationLabel(data.avgChatTime);
								},
							},
							{ label: "Plataforma", key: "channel.platform.name", format: "string", dataPosition: "center", width: 0.5 },
							{ label: "Cuenta", key: "channel.label", format: "string", headerPosition: "left", dataPosition: "left", width: 16 },
						]}
						totalRows={filteredCount}
						filteredPrintData={filteredPrintData}
						defaultParams={defaultParams}
						cafParams={{ ...tableParams, ...appliedFilters }}
						dataHandler={handlerData}
						print={{
							printHeader: {
								company: companyName,
								title: "Detallado Conversación",
								user: userName,
							},
						}}
						searcher={{
							searchable: true,
							searchPosition: "flex-start",
							searchDisabled: false,
							filterButton: false,
							width: "40rem",
						}}
						topButtons={renderTopButtons()}
					/>
				</div>
			</div>
			{isModalOpen && (
				<HistoricChatModal
					open={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setSelectedChatData(null);
					}}
					chatData={selectedChatData}
					companyName={companyName}
					userName={userName}
				/>
			)}
		</div>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		companyName: company.name,
	};
}

DetailedTable.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onOpenFilterModal: PropTypes.func,
	appliedFilters: PropTypes.object,
};

export default connect(mapStateToProps)(DetailedTable);
